import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { BzButton } from '../button/bz-button';
import { BzDialogLayout } from './bz-dialog-layout';
import { BZ_DIALOG_DATA, BzDialogRef } from './bz-dialog-ref';

/** Shared shape for the canned confirm and alert dialogs. */
export interface BzMessageDialogData {
  title: string;
  message: string;
  /** Defaults to "Confirm" for confirm, "OK" for alert. */
  confirmLabel?: string;
  /** Defaults to "Cancel". Ignored by `alert()`, which has one button. */
  cancelLabel?: string;
  /** Colours the confirm button for a destructive action. */
  destructive?: boolean;
  /** Set by the service — alert renders a single button. */
  mode?: 'confirm' | 'alert';
}

/**
 * The body behind `BzDialogService.confirm()` and `.alert()`.
 *
 * Not exported for direct use. Reach it through the service, which is the API
 * that survives a change of implementation — and which returns a plain promise
 * rather than making every caller wire up a dialog ref.
 */
@Component({
  selector: 'bz-message-dialog',
  imports: [BzDialogLayout, BzButton],
  templateUrl: './bz-message-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzMessageDialog {
  protected readonly data = inject<BzMessageDialogData>(BZ_DIALOG_DATA);
  private readonly ref = inject(BzDialogRef<boolean>);

  protected get isAlert(): boolean {
    return this.data.mode === 'alert';
  }

  protected close(result: boolean): void {
    this.ref.close(result);
  }
}
